import React, { useEffect } from "react";
import PropTypes from "prop-types";
import {
    ButtonLink,
    generateResolver,
    Modal,
    Notification,
    STATUS_TYPES,
    VALIDATORS,
    yup,
} from "dyl-components";
import { Form, Grid, Header, Icon, Button } from "semantic-ui-react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import FullScreenModalPopUp from "shared/FullScreenModalPopUp";
import useModal from "utils/useModal";
import { useDispatch, useSelector } from "react-redux";
import tagsCategoryActions from "actions/tag_categories";

const AddEditForm = ({ data, closeModal, setIsFormDirty }) => {
    const dispatch = useDispatch();
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty },
        reset,
    } = useForm({
        defaultValues: {
            categoryName: data?.category || "",
            description: data?.description || "",
            subTags: data?.tags?.length
                ? data?.tags?.map((tag) => ({
                      name: tag?.tag,
                      id: tag?.id,
                      isDeleted: false,
                  }))
                : [{ name: "", isDeleted: false }],
        },
        mode: "onBlur",
        resolver: generateResolver({
            categoryName: VALIDATORS.EVENT_NAME()
                .required("Category Name is required")
                .test(
                    "is-category-unique",
                    "Category Name already exists",
                    async function (value) {
                        const isEdit = !!data?.id;
                        if (isEdit && value === data?.category) return true;

                        try {
                            const payload = {
                                category: value,
                                subcategory: "",
                            };
                            const result = await dispatch(
                                tagsCategoryActions.checkTagCategoryOrSubcategoryAvailability(
                                    payload
                                )
                            );
                            return result?.category_available;
                        } catch (error) {
                            console.error(error);
                            return this.createError({
                                message: "Error checking category uniqueness",
                            });
                        }
                    }
                ),
            description: VALIDATORS.EVENT_DESCRIPTION(),
            subTags: yup
                .array()
                .of(
                    yup.object().shape({
                        name: yup.string().when("isDeleted", {
                            is: true,
                            then: (schema) => schema.nullable(true),
                            otherwise: (schema) =>
                                schema
                                    .required("Sub-Tag is required")
                                    .test(
                                        "is-subcategory-unique",
                                        "Sub-Tag already exists",
                                        async function (value) {
                                            if (!value?.trim?.()) return true;

                                            const isEdit = !!data?.id;
                                            const existingTag =
                                                data?.tags?.find?.(
                                                    (tag) =>
                                                        tag?.tag === value ||
                                                        this.parent?.isDeleted
                                                );

                                            if (isEdit && existingTag)
                                                return true;

                                            try {
                                                const payload = {
                                                    category: "",
                                                    subcategory: value,
                                                };
                                                const result = await dispatch(
                                                    tagsCategoryActions.checkTagCategoryOrSubcategoryAvailability(
                                                        payload
                                                    )
                                                );
                                                return result?.subcategory_available;
                                            } catch (error) {
                                                console.error(error);
                                                return this.createError({
                                                    message:
                                                        "Error checking sub-tag uniqueness",
                                                });
                                            }
                                        }
                                    ),
                        }),
                        isDeleted: yup.boolean(),
                    })
                )
                .min(1, "At least 1 sub-tag is required")
                .max(10),
        }),
    });

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: "subTags",
        keyName: "id",
    });
    const confirmationModal = useModal();
    const mode = data && Object.keys(data).length ? "edit" : "add";
    const isRequiredSubTag = (fields) => {
        const fieldsDeleted = fields.filter(field => field.isDeleted)
        return (fields.length - 1) === fieldsDeleted.length
    }
    
    const onSubmit = async (formData) => {
        try {
            const isEdit = mode === "edit";
            const payload = {
                category: formData?.categoryName?.trim?.(),
                description: formData?.description?.trim?.(),
                sub_categories: formData?.subTags
                    ?.filter?.((tag) => !tag?.isDeleted)
                    ?.map?.(!isEdit ? (tag) => tag?.name : (tag) => ({
                        id: tag?.id,
                        name: tag?.name
                    })),
            };
            if (isEdit) {
                if (formData?.subTags?.some?.((tag) => tag?.isDeleted)) {
                    return confirmationModal?.openModal?.(payload);
                }
                await dispatch(
                    tagsCategoryActions.updateTagCategory(data?.id, payload)
                );
            } else await dispatch(tagsCategoryActions.addTagCategory(payload));
            dispatch(tagsCategoryActions.getListOfTagCategories());
            Notification.alert(
                `${isEdit ? "Updated" : "Added"} successfully`,
                STATUS_TYPES.SUCCESS
            );
            reset();
            closeModal();
        } catch (error) {
            console.error(error);
            Notification.alert("Something went wrong", STATUS_TYPES.ERROR);
        }
    };

    const onConfirm = async () => {
        try {
            remove(confirmationModal?.data);
            const payload = {
                ...confirmationModal?.data,
            };
            await dispatch(
                tagsCategoryActions.updateTagCategory(data?.id, payload)
            );
            setTimeout(() => {
                dispatch(tagsCategoryActions.getListOfTagCategories());
                Notification.alert(
                    "Updated successfully",
                    STATUS_TYPES.SUCCESS
                );
            }, 500);
            reset();
            confirmationModal?.closeModal();
            closeModal?.();
        } catch (error) {
            console.error(error);
            Notification.alert("Something went wrong", STATUS_TYPES.ERROR);
        }
    };

    useEffect(() => setIsFormDirty(isDirty), [isDirty, setIsFormDirty]);

    const isSaving = useSelector(state => state.tag_categories.isAddingTagCategory || state.tag_categories.isUpdatingTagCategory || state.tag_categories.isDeletingTagCategory);
    return (
        <React.Fragment>
            <FullScreenModalPopUp
                header={"Are you sure?"}
                subheader="Sub-tags associated with calls will be removed permanently."
                isOpen={confirmationModal?.isVisible}
                onConfirm={onConfirm}
                onFormClose={confirmationModal?.closeModal}
                closeIcon={false}
            />

            <Modal.Header>
                {mode === "add" ? "Add" : "Edit"} Call Tag
            </Modal.Header>

            <Modal.Content scrolling style={{ padding: "2rem 2rem" }}>
                <Form noValidate loading={isSaving}>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column>
                                <Controller
                                    name="categoryName"
                                    control={control}
                                    render={({
                                        field,
                                        fieldState: { error },
                                    }) => (
                                        <Form.Input
                                            {...field}
                                            width={8}
                                            label="Category Name"
                                            error={error && error.message}
                                            required
                                        />
                                    )}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({
                                        field,
                                        fieldState: { error },
                                    }) => (
                                        <Form.TextArea
                                            {...field}
                                            width={8}
                                            label="Description"
                                            error={error && error.message}
                                        />
                                    )}
                                />
                            </Grid.Column>
                        </Grid.Row>
                        <Header color="blue" as="h3">
                            Sub-Tags
                        </Header>
                        {fields?.map?.((item, index) => (
                            <Grid.Row key={item?.id} columns={2}>
                                <Grid.Column width={8}>
                                    <Controller
                                        name={`subTags[${index}].name`}
                                        control={control}
                                        render={({
                                            field,
                                            fieldState: { error },
                                        }) => (
                                            <Form.Input
                                                {...field}
                                                label="Sub-Tag Name"
                                                error={
                                                    !item?.isDeleted &&
                                                    error &&
                                                    error.message
                                                }
                                                disabled={item?.isDeleted}
                                                placeholder={`
                                                        Enter tag name ${
                                                            index + 1
                                                        }`}
                                                required
                                            />
                                        )}
                                    />
                                </Grid.Column>
                                {(fields?.length > 1) && (
                                    <Grid.Column textAlign="bottom">
                                        {item?.isDeleted ? (
                                            <Icon
                                                className="fa-sharp fa-solid fa-arrow-rotate-left Pipeline__stage-undo-icon"
                                                size="large"
                                                link
                                                onClick={() => {
                                                    update(index, {
                                                        ...item,
                                                        isDeleted: false,
                                                    });
                                                }}
                                            />
                                        ) : (
                                            <Icon
                                                className="fa-solid fa-trash Pipeline__stage-remove-icon"
                                                size="large"
                                                link
                                                onClick={() => {
                                                    update(index, {
                                                        ...item,
                                                        isDeleted: true,
                                                    });
                                                }}
                                                disabled={isRequiredSubTag(fields)}
                                            />
                                        )}
                                    </Grid.Column>
                                )}
                            </Grid.Row>
                        ))}
                        <ButtonLink
                            onClick={() =>
                                append({ name: "", isDeleted: false })
                            }
                            disabled={fields?.length >= 10 || isSaving}
                            loading={isSaving}
                        >
                            <Icon size="small" name="plus" color="grey" />
                            Add New Sub-Tag
                        </ButtonLink>
                    </Grid>
                </Form>
            </Modal.Content>

            <Modal.Actions>
                <Button
                    disabled={!isValid || !isDirty}
                    primary
                    onClick={handleSubmit(onSubmit)}
                >
                    Save
                </Button>
            </Modal.Actions>
        </React.Fragment>
    );
};

AddEditForm.propTypes = {
    data: PropTypes.object,
    setIsFormDirty: PropTypes.func,
    closeModal: PropTypes.func,
};

export default AddEditForm;
